import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  CloudArrowUp, FilePdf, Image as ImageIcon, Trash, DownloadSimple,
  Star, StarHalf, CaretLeft, CaretRight, X, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { attachmentBlobUrl, attachmentPreviewUrl } from "@/lib/attachments";

const MAX_MB = 20;

const KIND_KEYS = ["", "Front", "Back", "Side", "Detail", "Campaign", "Lookbook", "Sample", "Technical", "Invoice", "Contract", "Other"];

function Tile({ a, previewUrl, canEdit, onOpen, onDelete, onDownload, onPrimary, onKind, onMove, t, tEnum, isFirst, isLast }) {
  return (
    <div
      data-testid={`attachment-tile-${a.id}`}
      className="group relative border overflow-hidden"
      style={{ borderColor: a.is_primary ? "var(--nylsu-gold)" : "var(--nylsu-border)", background: "var(--nylsu-card)" }}
    >
      <div className="aspect-square flex items-center justify-center overflow-hidden bg-[#F4F3ED] relative">
        {a.is_image ? (
          previewUrl ? (
            <img src={previewUrl} alt={a.original_filename} loading="lazy"
              className="w-full h-full object-cover cursor-zoom-in"
              onClick={() => onOpen(a)} />
          ) : <ImageIcon size={28} weight="light" className="text-[#8F8D84]" />
        ) : (
          <button type="button" onClick={() => onOpen(a)} className="flex flex-col items-center justify-center text-[#5C5A54] hover:text-[#1A1A1A] transition-colors" data-testid={`attach-open-${a.id}`}>
            <FilePdf size={36} weight="light" />
            <span className="mt-2 text-xs font-mono truncate max-w-[80%]">PDF</span>
          </button>
        )}
        {a.is_primary && (
          <span
            data-testid={`attach-primary-badge-${a.id}`}
            className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.2em] px-2 py-1"
            style={{ background: "var(--nylsu-gold)", color: "#1A1A1A" }}
          >
            {t("attach.primary")}
          </span>
        )}
      </div>

      <div className="p-2 space-y-1">
        <div className="flex items-center gap-1 justify-between text-[10px] text-[#8F8D84]">
          <span className="truncate max-w-[70%]" title={a.original_filename}>{a.original_filename}</span>
          <div className="flex gap-1">
            <button type="button" onClick={() => onDownload(a)} className="p-1 hover:text-[#1A1A1A]" aria-label={t("common.download")} data-testid={`attach-download-${a.id}`}>
              <DownloadSimple size={14} weight="light" />
            </button>
            {canEdit && (
              <button type="button" onClick={() => onDelete(a)} className="p-1 hover:text-[#8C3B3B]" aria-label={t("common.delete")} data-testid={`attach-delete-${a.id}`}>
                <Trash size={14} weight="light" />
              </button>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-1">
            <button type="button"
              onClick={() => onPrimary(a)}
              data-testid={`attach-set-primary-${a.id}`}
              className="p-1 transition-colors"
              style={{ color: a.is_primary ? "var(--nylsu-gold)" : "#8F8D84" }}
              aria-label={t("attach.setPrimary")}
              title={t("attach.setPrimary")}
            >
              <Star size={14} weight={a.is_primary ? "fill" : "light"} />
            </button>
            <select
              value={a.kind || ""}
              onChange={(e) => onKind(a, e.target.value)}
              data-testid={`attach-kind-${a.id}`}
              className="flex-1 bg-transparent text-[10px] uppercase tracking-[0.1em] outline-none py-0.5"
            >
              {KIND_KEYS.map((k) => (
                <option key={k || "none"} value={k}>{k ? tEnum("attach.kind", k) : t("attach.noKind")}</option>
              ))}
            </select>
            <button type="button" onClick={() => onMove(a, -1)} disabled={isFirst} className="p-1 disabled:opacity-20 text-[#8F8D84] hover:text-[#1A1A1A]" aria-label="Move left" data-testid={`attach-move-up-${a.id}`}>
              <CaretLeft size={12} weight="light" />
            </button>
            <button type="button" onClick={() => onMove(a, 1)} disabled={isLast} className="p-1 disabled:opacity-20 text-[#8F8D84] hover:text-[#1A1A1A]" aria-label="Move right" data-testid={`attach-move-down-${a.id}`}>
              <CaretRight size={12} weight="light" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Lightbox({ items, index, previews, onClose, onIndex, t }) {
  const cur = items[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onClose, onIndex]);

  if (!cur) return null;
  const url = previews[cur.id];

  return (
    <div
      data-testid="attachment-lightbox"
      className="fixed inset-0 z-[60] flex items-center justify-center p-8"
      style={{ background: "rgba(20,20,20,0.92)" }}
      onClick={onClose}
    >
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="lightbox-close"
        className="absolute top-6 right-6 text-white/80 hover:text-white" aria-label={t("common.close")}>
        <X size={22} weight="light" />
      </button>

      {items.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + items.length) % items.length); }}
            data-testid="lightbox-prev"
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3" aria-label="Previous">
            <CaretLeft size={26} weight="light" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % items.length); }}
            data-testid="lightbox-next"
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3" aria-label="Next">
            <CaretRight size={26} weight="light" />
          </button>
        </>
      )}

      <div className="max-h-full max-w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        {cur.is_image ? (
          url ? <img src={url} alt={cur.original_filename} className="max-h-[80vh] max-w-[85vw] object-contain" />
              : <div className="text-white/70 text-sm">Loading…</div>
        ) : (
          url ? (
            <iframe title={cur.original_filename} src={url} className="w-[85vw] h-[80vh] bg-white" />
          ) : <div className="text-white/70 text-sm">Loading…</div>
        )}
        <div className="text-white/85 text-xs font-mono flex items-center gap-4">
          <span>{cur.original_filename}</span>
          {items.length > 1 && <span className="text-white/50">{index + 1} / {items.length}</span>}
        </div>
      </div>
    </div>
  );
}

export default function AttachmentGallery({ entity, entityId, editable = true, compact = false }) {
  const { can } = useAuth();
  const { t, tEnum } = useI18n();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState({});
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const canEdit = editable && can(`${entity}:edit`);

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get("/attachments", { params: { entity, entity_id: entityId } });
      setItems(data);
    } catch (e) {
      if (![403, 404].includes(e?.response?.status)) toast.error(formatApiError(e));
      setItems([]);
    }
  }, [entity, entityId]);

  useEffect(() => { load(); }, [load]);

  // Fetch blob previews (images AND PDFs) as blob URLs for inline preview
  useEffect(() => {
    const created = [];
    (async () => {
      for (const a of items) {
        if (previews[a.id]) continue;
        try {
          const url = await attachmentBlobUrl(a.id);
          if (url) {
            created.push(url);
            setPreviews((p) => ({ ...p, [a.id]: url }));
          }
        } catch {}
      }
    })();
    return () => created.forEach((u) => attachmentPreviewUrl.release(u));
    // eslint-disable-next-line
  }, [items]);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const f of files) {
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} > ${MAX_MB} MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/attachments", fd, {
          params: { entity, entity_id: entityId },
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(t("attach.uploadedToast", { name: f.name }));
      } catch (e) { toast.error(formatApiError(e)); }
    }
    setBusy(false);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!canEdit) return;
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const onDelete = async (a) => {
    if (!window.confirm(t("attach.deleteConfirm", { name: a.original_filename }))) return;
    try { await api.delete(`/attachments/${a.id}`); toast.success(t("common.deletedToast")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onDownload = async (a) => {
    try {
      const r = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a"); link.href = url; link.download = a.original_filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onPrimary = async (a) => {
    try {
      await api.patch(`/attachments/${a.id}`, { is_primary: !a.is_primary });
      toast.success(t(a.is_primary ? "attach.unsetPrimaryToast" : "attach.setPrimaryToast"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onKind = async (a, kind) => {
    try { await api.patch(`/attachments/${a.id}`, { kind: kind || "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onMove = async (a, dir) => {
    const idx = items.findIndex((x) => x.id === a.id);
    const swap = items[idx + dir];
    if (!swap) return;
    try {
      await Promise.all([
        api.patch(`/attachments/${a.id}`, { sort_order: swap.sort_order ?? idx + dir }),
        api.patch(`/attachments/${swap.id}`, { sort_order: a.sort_order ?? idx }),
      ]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openIdx = (a) => setLightboxIdx(items.findIndex((x) => x.id === a.id));

  if (!entityId) {
    return <div className="text-xs text-[#8F8D84] italic">{t("attach.savePrompt")}</div>;
  }

  return (
    <div data-testid={`attachment-gallery-${entity}-${entityId}`}>
      {canEdit && (
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="attachment-dropzone"
          className="block border border-dashed p-6 text-center cursor-pointer hover:border-[#C2A878] transition-colors"
          style={{ borderColor: "var(--nylsu-border-strong)" }}
        >
          <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="attachment-file-input"
            onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
          <CloudArrowUp size={22} weight="light" className="mx-auto text-[#8F8D84]" />
          <div className="mt-2 text-sm text-[#5C5A54]">{busy ? t("attach.uploading") : t("attach.drop")}</div>
          <div className="mt-1 overline">{t("attach.constraints", { mb: MAX_MB })}</div>
        </label>
      )}

      {items.length === 0 ? (
        <div className={`text-xs text-[#8F8D84] ${canEdit ? "mt-4" : ""}`}>{t("attach.empty")}</div>
      ) : (
        <div className={`grid gap-4 mt-4 ${compact ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"}`}>
          {items.map((a, i) => (
            <Tile key={a.id} a={a} previewUrl={previews[a.id]} canEdit={canEdit}
              onOpen={openIdx} onDelete={onDelete} onDownload={onDownload}
              onPrimary={onPrimary} onKind={onKind} onMove={onMove}
              t={t} tEnum={tEnum} isFirst={i === 0} isLast={i === items.length - 1} />
          ))}
        </div>
      )}

      {lightboxIdx != null && (
        <Lightbox items={items} index={lightboxIdx} previews={previews}
          onClose={() => setLightboxIdx(null)} onIndex={setLightboxIdx} t={t} />
      )}
    </div>
  );
}
